/**
 * SrOpfsStream — main-thread handle on opfs-stream-worker.js.
 *
 *   SrOpfsStream.supported()            → the page has the worker URLs and OPFS
 *   SrOpfsStream.warm()                 → spawn the worker ahead of open() (the Record click)
 *   SrOpfsStream.open(name, {onError})  → Promise<writer>  (rejects → caller falls back to createWritable)
 *   writer.write(data, at)              → fire-and-forget positional write (the muxer's onData)
 *   writer.finalize({flatten})          → Promise<{size, sizeBefore, hwm, flatten}>  — flush, flatten fMP4 → flat MP4 in place, close
 *                                         (rejects with name 'StorageEvicted' when the browser took the file away)
 *   writer.abort({remove})              → Promise
 *   writer.kill()                       → terminate a worker that stopped answering (frees the handle)
 *   writer.bytesWritten                 → high-water mark handed to the worker
 *   SrOpfsStream.recover(name)          → Promise<{ok, reason, size, durationMs, fragments, dropped}>
 *   SrOpfsStream.openWav({label, sampleRate, channels}) → Promise<wav>  — a WAV through the same lane
 *                                         (the recorder's «Голос и звук отдельно»; see openWav below)
 *
 * The worker script and the Mp4Flatten dependency come from the layout as
 * versioned URLs (window.__srWorkerBase.opfsStream / .mp4Flatten): a bare
 * /js/… path would keep a returning visitor on last year's worker for a year.
 */
(function (w) {
    'use strict';
    if (w.SrOpfsStream) return;

    function urls() { var b = w.__srWorkerBase || {}; return { worker: b.opfsStream, flatten: b.mp4Flatten }; }
    function supported() {
        var u = urls();
        return typeof Worker === 'function' && !!u.worker && !!u.flatten &&
            typeof navigator !== 'undefined' && !!navigator.storage && typeof navigator.storage.getDirectory === 'function';
    }

    function Channel() {
        var self = this;
        this.worker = new Worker(urls().worker);
        this.pending = {}; this.nextId = 1; this.onError = null;
        this.worker.onmessage = function (ev) {
            var m = ev.data || {};
            if (m.op === 'error') { if (self.onError) { try { self.onError(m); } catch (_) {} } return; }
            var p = m.id != null ? self.pending[m.id] : null;
            if (!p) return;
            delete self.pending[m.id];
            if (m.op === 'fail') { var e = new Error(m.message || m.name || 'opfs-stream'); e.name = m.name || 'Error'; e.closed = !!m.closed; p.reject(e); }
            else p.resolve(m);
        };
        this.worker.onerror = function (ev) {
            var e = new Error((ev && ev.message) || 'worker-error');
            for (var k in self.pending) { if (Object.prototype.hasOwnProperty.call(self.pending, k)) { self.pending[k].reject(e); delete self.pending[k]; } }
        };
    }
    Channel.prototype.request = function (msg) {
        var self = this, id = this.nextId++;
        return new Promise(function (resolve, reject) {
            self.pending[id] = { resolve: resolve, reject: reject };
            msg.id = id;
            try { self.worker.postMessage(msg); } catch (e) { delete self.pending[id]; reject(e); }
        });
    };
    Channel.prototype.terminate = function () { try { this.worker.terminate(); } catch (_) {} };

    // A worker spawned in advance. Opened at the moment the take starts, the
    // writer took 1.8 s (Yandex 26.8) to 6.5 s (Chrome 154, busy machine),
    // measured 26.09.2026 — of which the file work inside the worker was
    // 12–17 ms; the rest is the worker's start and the round trip to a tab the
    // browser has already hidden (it switches to the captured tab). All of it
    // is screen the user saw happen and the file did not have. warm() is
    // called on the Record click, so the worker starts while the user is still
    // choosing what to share; `openStats` reports what is left (warmed or cold,
    // whole wait, file work) so the field says how much that saved.
    var spare = null;
    function warm() {
        if (spare || !supported()) return;
        try {
            var ch = new Channel();
            spare = { ch: ch };
            ch.request({ op: 'ping', flattenUrl: urls().flatten }).catch(function () {});
        } catch (_) { spare = null; }
    }

    async function open(name, opts) {
        opts = opts || {};
        var t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
        var warmed = !!spare;
        var ch = spare ? spare.ch : new Channel();
        spare = null;
        ch.onError = function (m) {
            if (typeof opts.onError === 'function') {
                var e = new Error(m.message || 'write failed'); e.name = m.name || 'Error'; e.at = m.at;
                opts.onError(e);
            }
        };
        var r = await ch.request({ op: 'open', name: name });
        if (r.op === 'unsupported') { ch.terminate(); var ue = new Error('sync access handle unavailable'); ue.name = 'NotSupportedError'; throw ue; }
        var writer = {
            name: name,
            // how the open went: a warmed worker, the whole wait, the file ops inside the worker
            openStats: { warmed: warmed, ms: Math.round(((typeof performance !== 'undefined') ? performance.now() : Date.now()) - t0), fileMs: r.ms || 0 },
            bytesWritten: 0,
            write: function (data, at) {
                // the muxer may reuse its buffers after onData returns — copy, then hand the copy over.
                // A copy that cannot be made or a post that throws is a lost stretch of the
                // take: said as a storage failure (the page stops the take), never swallowed.
                try {
                    var copy = data.slice ? data.slice() : new Uint8Array(data);
                    writer.bytesWritten = Math.max(writer.bytesWritten, at + copy.byteLength);
                    ch.worker.postMessage({ op: 'write', data: copy, at: at }, [copy.buffer]);
                } catch (e) {
                    if (!writer.postFailed++ && ch.onError) { try { ch.onError({ name: (e && e.name) || 'PostError', message: 'write not handed to the worker: ' + ((e && e.message) || ''), at: at }); } catch (_) {} }
                }
            },
            postFailed: 0,
            finalize: function (o) {
                o = o || {};
                return ch.request({ op: 'finalize', flatten: !!o.flatten, flattenUrl: urls().flatten })
                    .then(function (m) { ch.terminate(); return { size: m.size, sizeBefore: m.sizeBefore || 0, hwm: m.hwm || 0, flatten: m.flatten }; },
                          function (e) { ch.terminate(); throw e; });
            },
            abort: function (o) {
                o = o || {};
                return ch.request({ op: 'abort', remove: !!o.remove }).then(function () { ch.terminate(); }, function () { ch.terminate(); });
            },
            // a worker that stopped answering: terminating it releases the sync
            // access handle, so the file can be read as it lies on disk
            kill: function () { ch.terminate(); }
        };
        return writer;
    }

    async function recover(name) {
        var ch = new Channel();
        try {
            var m = await ch.request({ op: 'recover', name: name, flattenUrl: urls().flatten });
            return { ok: !!m.ok, reason: m.reason || '', message: m.message || '', size: m.size || 0, durationMs: m.durationMs || 0, fragments: m.fragments || 0, dropped: m.dropped || 0, ms: m.ms || 0 };
        } catch (e) {
            return { ok: false, reason: (e && e.name) || 'error', message: (e && e.message) || '' };
        } finally { ch.terminate(); }
    }

    // «Голос и звук отдельно» (26.10.2026): a WAV per source, written while the
    // take runs. Until 30.10 every emitted block (10–20 ms of audio) became its
    // own createWritable() write in one unbounded promise chain on the page; on
    // a slow disk the chain outgrew the take, held the raw samples in memory and
    // starved the capture readers — RU 26–30.10, long takes with mic + system
    // sound: with the tracks on 10 of 14 had > 20 % of the take filled with
    // silence and Stop took 8 s … 14 min; without them 2 of 61 and 1.
    // Now: quantised here in half-second batches (no chain, nothing awaited),
    // written by a worker of this lane at their place in the file, the header
    // patched at finalize. A refused write ends THAT track, never the take.
    //   SrOpfsStream.openWav({label, sampleRate, channels}) → Promise<wav>
    //   wav.write([Float32Array per channel]) → false once the track has failed
    //   wav.failed() → '' | reason;  wav.finalize() → Promise<Blob audio/wav>;  wav.abort()
    async function openWav(o) {
        var OS = w.OutputSink, Q = w.__pcm16Quantizer;
        if (!OS || typeof OS.wavHeader !== 'function' || typeof OS.diskName !== 'function' || typeof Q !== 'function') {
            var ue = new Error('wav-unavailable'); ue.name = 'NotSupportedError'; throw ue;
        }
        var ch = o.channels | 0, sr = o.sampleRate | 0, bad = '';
        var name = OS.diskName(o.label || 'wav');
        var wr = await open(name, { onError: function (e) { bad = bad || ('write:' + ((e && e.name) || 'error')); } });
        var headLen = OS.wavHeader(ch, sr, 16, 0).length;
        wr.write(OS.wavHeader(ch, sr, 16, 0), 0);
        var maxData = 0xFFFFFFFF - (headLen - 8), dataBytes = 0;
        // Stems are summed later in an editor: rounding without noise (docs/why/pcm16-quantize.md).
        var q = Q(false);
        var cap = sr * ch, batch = new Int16Array(cap), fill = 0;
        function flush() {
            if (!fill) return;
            var out = new Uint8Array(fill * 2);
            out.set(new Uint8Array(batch.buffer, 0, fill * 2));
            wr.write(out, headLen + dataBytes);
            dataBytes += out.byteLength; fill = 0;
        }
        return {
            name: name,
            failed: function () { return bad; },
            write: function (planes) {
                if (bad) return false;
                var n = planes && planes.length ? planes[0].length : 0;
                if (!n) return true;
                if (dataBytes + (fill + n * ch) * 2 > maxData) { bad = 'wav_4gib'; return false; }
                for (var off = 0; off < n;) {
                    if (fill >= cap) flush();
                    var k = Math.min(n - off, (cap - fill) / ch);
                    for (var c = 0; c < ch; c++) q(planes[c].subarray(off, off + k), k, batch, fill + c, ch);
                    fill += k * ch; off += k;
                }
                if (fill * 2 >= cap) flush();
                return true;
            },
            finalize: async function () {
                flush();
                wr.write(OS.wavHeader(ch, sr, 16, dataBytes), 0);
                await wr.finalize({ flatten: false });
                var root = await navigator.storage.getDirectory();
                var file = await (await root.getFileHandle(name)).getFile();
                if (file.size !== headLen + dataBytes) {
                    var se = new Error('wav-short ' + file.size + '/' + (headLen + dataBytes)); se.code = 'WAV_SHORT'; throw se;
                }
                return new Blob([file], { type: 'audio/wav' });
            },
            abort: function () { return wr.abort({ remove: true }); }
        };
    }

    w.SrOpfsStream = { supported: supported, warm: warm, open: open, recover: recover, openWav: openWav };
})(window);
